import type { DialogProps } from '@mui/material/Dialog';

import { z as zod } from 'zod';
import { toast } from 'sonner';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';

import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { parent_activityFunc } from 'src/context/activity/parent_activity';
import { anonymous_activityBlogFunc } from 'src/context/blog/anonymous_activityBlog';
import { userRateBlogMutationFunc } from 'src/context/user_rateListing/user_rateBlog';
import { userRateListingMutationFunc } from 'src/context/user_rateListing/user_rateListing';

import { Form, Field } from 'src/components/hook-form';

// ----------------------------------------------------------------------

export type ReviewSchemaType = zod.infer<typeof ReviewSchema>;

export const ReviewSchema = zod
  .object({
    rating: zod.number().optional(),
    review: zod.string().optional(),
  })
  .refine(
    (data) => (data.rating && data.rating > 0) || (data.review && data.review.trim().length > 0),
    {
      message: 'Lütfen en az bir yıldız verin veya yorum yazın',
      path: ['rating'], // Bu hata rating alanında gösterilecek
    }
  );

// ----------------------------------------------------------------------

type Props = DialogProps & {
  onClose: () => void;
  isLogin: boolean;
  listingId: string;
  activityId: string;
  entityType: string;
};

interface IRateListing {
  listingId: string;
  rating: number;
  comment: string;
}

interface IRateBlog {
  blogId: string;
  rating: number;
  comment: string;
}

export function ProductReviewNewForm({
  activityId,
  listingId,
  onClose,
  isLogin,
  entityType,
  ...other
}: Props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const defaultValues: ReviewSchemaType = {
    rating: 0,
    review: '',
  };

  const methods = useForm<ReviewSchemaType>({
    mode: 'all',
    resolver: zodResolver(ReviewSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const watchedValues = watch();
  const isFormValid =
    (watchedValues.rating && watchedValues.rating > 0) ||
    (watchedValues.review && watchedValues.review.trim().length > 0);

  const onSubmit = handleSubmit(async (data) => {
    try {
      let result;

      if (entityType === 'aktivite') {
        result = await dispatch(
          userRateListingMutationFunc({
            rateListingInput: {
              listingId,
              rating: data?.rating ?? null,
              comment: data?.review ?? null,
            } as IRateListing,
          })
        );
      } else if (entityType === 'blog') {
        result = await dispatch(
          userRateBlogMutationFunc({
            rateBlogInput: {
              blogId: activityId,
              rating: data?.rating ?? null,
              comment: data?.review ?? null,
            } as IRateBlog,
          })
        );
      }

      // Response kontrolü
      if ((result as any)?.payload?.errors) {
        console.warn((result as any)?.payload?.errors);
        if ((result as any)?.payload?.errors?.[0]?.message?.includes('already')) {
          toast.error('Bir aktiviteye yalnızca bir kez yorum yapabilirsiniz', { position: 'bottom-right' });
        } else {
          toast.error('Yorumunuz gönderilirken hata oluştu', { position: 'bottom-right' });
        }
      } else if ((result as any)?.payload?.data) {
        toast.success('Yorumunuz gönderildi', { position: 'bottom-right' });

        reset();
        onClose();

        // Kısa bir gecikme ile verilerini yenile
        setTimeout(() => {
          if (entityType === 'blog') {
            dispatch(
              anonymous_activityBlogFunc({
                activityBlogId: activityId,
              })
            );
          } else {
            dispatch(
              parent_activityFunc({
                activityId,
              })
            );
          }
        }, 1000);
      }
    } catch (error) {
      console.warn(error);
      toast.error('Yorumunuz gönderilirken hata oluştu', { position: 'bottom-right' });
    }
  });

  const onCancel = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  return (
    <Dialog onClose={onClose} {...other}>
      <Form methods={methods} onSubmit={onSubmit}>
        <DialogTitle> Yorum Ekle </DialogTitle>

        <DialogContent sx={{ minWidth: 400 }}>
          {isLogin ? (
            <>
              <div>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Aktivite hakkında yorumunuz:
                </Typography>
                <Field.Rating name="rating" />
              </div>
              <Field.Text name="review" label="Yorumunuz" multiline rows={3} sx={{ mt: 3 }} />
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mb: 3,
              }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                Aktiviteye yorum yazmak ve puan verebilmek için lütfen giriş yapınız.
              </Typography>

              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  navigate('/auth/jwt/sign-in');
                }}
              >
                Giriş Yap
              </Button>
            </Box>
          )}
        </DialogContent>

        {isLogin && (
          <DialogActions>
            <Button color="inherit" variant="outlined" onClick={onCancel}>
              Vazgeç
            </Button>

            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={!isFormValid}
            >
              Onayla
            </LoadingButton>
          </DialogActions>
        )}
      </Form>
    </Dialog>
  );
}
